import { defineStore } from 'pinia';
import type { Notification, NotificationCounts } from '@its/shared';
import { api } from '@/lib/api';

export type NotificationTab = 'en_cours' | 'terminees';

/**
 * LE FIL auquel une notification donne accès.
 *
 * Un échange se tient toujours sur la notification D'ORIGINE : les pings
 * « Réponse de X » la désignent par leur `entityId`. La règle est la même en
 * base (`THREAD_OF`, cf. `notificationRepo`) — elle vit ici, et non recopiée
 * dans l'écran, pour que le compte et l'affichage ne puissent pas diverger.
 */
export const threadKeyOf = (n: Notification): string =>
  (n.type === 'echange_notification' ? n.entityId : n.id) ?? n.id;

interface State {
  items: Notification[];
  counts: NotificationCounts;
  tab: NotificationTab;
  loading: boolean;
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): State => ({
    items: [],
    counts: { enCours: 0, terminees: 0, nonLues: 0, depuisConnexion: 0 },
    tab: 'en_cours',
    loading: false,
  }),
  actions: {
    async fetch(tab?: NotificationTab): Promise<void> {
      const target = tab ?? this.tab;
      this.loading = true;
      this.tab = target;
      try {
        const res = await api.get<{ notifications: Notification[]; counts: NotificationCounts }>(
          `/notifications?tab=${target}`,
        );
        this.items = res.notifications;
        this.counts = res.counts;
      } finally {
        this.loading = false;
      }
    },
    /** Pastille de la cloche — léger, appelé au démarrage et après action. */
    async refreshCounts(): Promise<void> {
      try {
        const { counts } = await api.get<{ counts: NotificationCounts }>('/notifications/counts');
        this.counts = counts;
      } catch {
        // silencieux : la pastille n'est pas critique
      }
    },
    async markRead(id: string): Promise<void> {
      const { notification } = await api.post<{ notification: Notification }>(`/notifications/${id}/read`);
      this.items = this.items.map((n) => (n.id === id ? notification : n));
      await this.refreshCounts();
    },
    async markDone(id: string): Promise<void> {
      await api.post(`/notifications/${id}/done`);
      // Quitte l'onglet « en cours ».
      if (this.tab === 'en_cours') this.items = this.items.filter((n) => n.id !== id);
      await this.refreshCounts();
    },
    /**
     * Recale le nombre de messages d'un FIL, sur toutes les lignes qui y
     * mènent. Le compte vient de la liste ; après avoir ouvert ou alimenté une
     * conversation, on le corrige sur place plutôt que de recharger la page —
     * et on le corrige partout, car un même fil peut occuper plusieurs lignes.
     */
    setThreadMessageCount(threadId: string, count: number): void {
      this.items = this.items.map((n) =>
        threadKeyOf(n) === threadId && n.messageCount !== count ? { ...n, messageCount: count } : n,
      );
    },
    async markAllRead(): Promise<void> {
      await api.post('/notifications/read-all');
      await this.fetch(this.tab);
    },
  },
});
